/**
 * PostOpsTab.tsx
 *
 * Patient-detail Post-ops section, ported from legacy
 * lib/patient-details/postop-details.php and its two AJAX panels
 * (postop-details-status.php, postop-details-qa.php).
 *
 * A plan legitimately has no post-op record until someone saves for the first
 * time — legacy creates the row lazily on any write — so the tab renders the
 * not-yet-started shape rather than an error.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, Button, ConfirmDialog } from '@/components/ui';
import { toast } from '@/store/toastStore';
import { looksLikeLeakyInternalError } from '@/services/apiClient';
import { getPlansByPatient } from '@/services/plans.service';
import {
  getPostOpsRecord,
  savePostOpsRecord,
  setPostOpsQaResolved,
  setPostOpsReasons,
  type PostOpsRecord,
  type PostOpsRecordChanges,
  type PostOpsRecordResponse,
} from '@/services/postOpsRecord.service';
import { formatPostOpsDate } from '@/pages/post-ops/postOpsShared';
import { PostOpsFilesCard } from './post-ops/PostOpsFilesCard';

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <h3 className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900">
        {title}
      </h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

const CHECKBOX_CLASS = 'h-4 w-4 rounded border-slate-300';
const FIELD_CLASS = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:opacity-50';

// Characters outside the Basic Multilingual Plane (emoji). The note columns are
// utf8mb3, so the API strips these before saving (PostOpsWriteService::sanitize).
const SUPPLEMENTARY_CHARS = /[\u{10000}-\u{10FFFF}]/u;

/** Whether a note the user sent came back with its emoji removed. */
function noteWasStripped(sent: string | undefined, saved: string): boolean {
  return sent !== undefined && sent !== saved && SUPPLEMENTARY_CHARS.test(sent);
}

const NOTE_SAVE_FALLBACK = 'Your change was not saved. Try again.';

/**
 * The server's reason when it gave a deliberate one (e.g. the module is off).
 * A server error's own wording is internal ("Server Error", a database
 * message), so a 5xx or a known leak shape gets the retry message instead.
 */
function noteSaveErrorMessage(error: unknown): string {
  const e = error as { userMessage?: string; response?: { status?: number } } | null;
  const message = e?.userMessage;
  const status = e?.response?.status ?? 0;
  if (!message || status >= 500 || looksLikeLeakyInternalError(message)) return NOTE_SAVE_FALLBACK;
  return message;
}

interface PostOpsTabProps {
  patientId: number;
  planId?: number;
}

export function PostOpsTab({ patientId, planId: routedPlanId }: PostOpsTabProps) {
  const queryClient = useQueryClient();
  const [confirmReopen, setConfirmReopen] = useState(false);

  // A patient can carry several plans and only one of them is the case being
  // followed up, so the tab resolves its own plan rather than dead-ending when
  // the page was opened without one in the URL — the route plan, else the
  // patient's only plan, else a selector. Mirrors SurgeryTab.
  const { data: patientPlans } = useQuery({
    queryKey: ['patient-plans', patientId],
    queryFn: () => getPlansByPatient(patientId),
    enabled: Number.isFinite(patientId) && patientId > 0,
  });

  const [chosenPlanId, setChosenPlanId] = useState<number | undefined>(routedPlanId);

  const planId =
    chosenPlanId ?? routedPlanId ?? (patientPlans?.length === 1 ? patientPlans[0].planId : undefined);

  const recordKey = ['post-ops-record', planId];

  const { data, isLoading, isError } = useQuery({
    queryKey: recordKey,
    queryFn: () => getPostOpsRecord(planId as number),
    enabled: typeof planId === 'number' && planId > 0,
    // Notes save on blur. A focus-refetch that brought back someone else's edit
    // would remount the textarea and throw away what is being typed.
    refetchOnWindowFocus: false,
  });

  /**
   * Take the record the server just wrote rather than only invalidating.
   *
   * The reason picker sends the WHOLE selected set, built from the cached
   * record. Re-enabling the checkboxes before a refetch landed meant a quick
   * second click sent a set computed from stale ids, and the server's
   * delete/insert diff removed the reason just added.
   */
  function applyRecord(record: PostOpsRecord) {
    queryClient.setQueryData<PostOpsRecordResponse>(recordKey, (prev) =>
      prev ? { ...prev, record } : prev,
    );
    queryClient.invalidateQueries({ queryKey: ['post-ops-list'] });
  }

  // Part of each note textarea's key, one per note. Bumped when that note's
  // emoji were stripped, because the saved text can equal what the record held
  // before (an emoji-only note on an empty record), and then the value-derived
  // key alone would not remount the textarea to drop what was typed. Separate
  // counters so stripping one note never remounts the other mid-typing.
  const [noteRevision, setNoteRevision] = useState(0);
  const [qaNoteRevision, setQaNoteRevision] = useState(0);

  const save = useMutation({
    mutationFn: (changes: PostOpsRecordChanges) => savePostOpsRecord(planId as number, changes),
    onSuccess: (response, changes) => {
      applyRecord(response.record);

      const noteStripped = noteWasStripped(changes.postopNote, response.record.postopNote);
      const qaNoteStripped = noteWasStripped(changes.qaNote, response.record.qaNote);
      if (noteStripped) setNoteRevision((n) => n + 1);
      if (qaNoteStripped) setQaNoteRevision((n) => n + 1);
      if (noteStripped || qaNoteStripped) {
        toast.info(
          'Emoji removed',
          "Emoji can't be saved in notes, so they were removed. The rest of your note was saved.",
        );
      }
    },
    onError: (error) => toast.error('Could not save', noteSaveErrorMessage(error)),
  });

  const qaResolved = useMutation({
    mutationFn: (resolved: boolean) => setPostOpsQaResolved(planId as number, resolved),
    onSuccess: (response) => applyRecord(response.record),
    onError: () => toast.error('Could not update Q/A', 'The Q/A status was not changed. Try again.'),
  });

  const reasons = useMutation({
    mutationFn: (reasonIds: number[]) => setPostOpsReasons(planId as number, reasonIds),
    onSuccess: (response) => applyRecord(response.record),
    onError: (error) =>
      toast.error(
        'Could not save reasons',
        (error as { userMessage?: string } | null)?.userMessage ?? 'Your reasons were not saved. Try again.',
      ),
  });

  const planSelector =
    (patientPlans?.length ?? 0) > 1 ? (
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">Plan</span>
        <select
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          value={planId ?? ''}
          aria-label="Plan"
          onChange={(e) => setChosenPlanId(Number(e.target.value) || undefined)}
        >
          {!planId && <option value="">Choose a plan...</option>}
          {patientPlans?.map((p) => (
            <option key={p.planId} value={p.planId}>
              {[p.displayId ?? p.planId, p.doctorName, p.stageName].filter(Boolean).join(' — ')}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  if (!planId) {
    return (
      <div className="space-y-4 py-2">
        {planSelector}
        <p className="py-4 text-sm text-slate-500">
          {(patientPlans?.length ?? 0) > 1
            ? 'Choose a plan to see its post-op follow-up.'
            : 'This patient has no plan to follow up.'}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 py-2">
        {planSelector}
        <p className="py-4 text-sm text-red-600">
          This post-op record could not be loaded. Refresh the page, or contact support if it keeps happening.
        </p>
      </div>
    );
  }

  const { record, reasonTypes, statusChoices } = data;
  const busy = save.isPending || reasons.isPending || qaResolved.isPending;

  function toggleReason(reasonId: number, checked: boolean) {
    const next = checked
      ? [...record.reasonIds, reasonId]
      : record.reasonIds.filter((id) => id !== reasonId);

    reasons.mutate(next);
  }

  return (
    <div className="space-y-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Post-ops</h2>
        <div className="flex flex-wrap items-center gap-3">
          {planSelector}
          {record.updatedAt && (
            <span className="text-xs text-slate-500">Last updated {formatPostOpsDate(record.updatedAt)}</span>
          )}
        </div>
      </div>

      <SectionCard title="Follow-Up Status">
        <div className="space-y-4">
          <div>
            <label htmlFor="postop-status" className="mb-1 block text-xs text-slate-500">
              Status
            </label>
            <select
              id="postop-status"
              className={FIELD_CLASS}
              // A record nobody has saved reads as 0, legacy's "Select one...",
              // exactly as legacy's own dropdown pre-selects it.
              value={record.status ?? 0}
              disabled={busy}
              onChange={(e) => save.mutate({ status: Number(e.target.value) })}
            >
              {statusChoices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="postop-note" className="mb-1 block text-xs text-slate-500">
              Note
            </label>
            <textarea
              id="postop-note"
              rows={4}
              className={FIELD_CLASS}
              defaultValue={record.postopNote}
              key={`note-${noteRevision}-${record.postopNote}`}
              onBlur={(e) => {
                if (e.target.value !== record.postopNote) save.mutate({ postopNote: e.target.value });
              }}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Quality Assurance">
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-slate-900">
            <input
              type="checkbox"
              className={CHECKBOX_CLASS}
              checked={record.isQa}
              disabled={busy}
              onChange={(e) => save.mutate({ wasQA: e.target.checked ? 1 : 0 })}
            />
            Flag this case for Q/A review
          </label>

          {record.isQa && (
            <>
              <div>
                <span className="mb-1 block text-xs text-slate-500">Reasons</span>
                {reasonTypes.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No Q/A reasons have been set up yet. Ask your administrator to add them.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {reasonTypes.map((reason) => (
                      <label key={reason.id} className="flex items-center gap-2 text-sm text-slate-900">
                        <input
                          type="checkbox"
                          className={CHECKBOX_CLASS}
                          checked={record.reasonIds.includes(reason.id)}
                          disabled={busy}
                          onChange={(e) => toggleReason(reason.id, e.target.checked)}
                        />
                        {reason.title}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="postop-qa-note" className="mb-1 block text-xs text-slate-500">
                  Q/A note
                </label>
                <textarea
                  id="postop-qa-note"
                  rows={3}
                  className={FIELD_CLASS}
                  defaultValue={record.qaNote}
                  key={`qa-note-${qaNoteRevision}-${record.qaNote}`}
                  onBlur={(e) => {
                    if (e.target.value !== record.qaNote) save.mutate({ qaNote: e.target.value });
                  }}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-slate-900">
                  {record.qaResolved ? `Resolved ${formatPostOpsDate(record.qaResolvedAt)}` : 'Not resolved'}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={qaResolved.isPending}
                  disabled={busy}
                  onClick={() => {
                    // Re-opening emails everyone following this plan, so it is
                    // confirmed; resolving is silent and needs no prompt.
                    if (record.qaResolved) setConfirmReopen(true);
                    else qaResolved.mutate(true);
                  }}
                >
                  {record.qaResolved ? 'Reopen' : 'Mark resolved'}
                </Button>
              </div>
            </>
          )}
        </div>
      </SectionCard>

      <PostOpsFilesCard patientId={patientId} />

      <ConfirmDialog
        isOpen={confirmReopen}
        onClose={() => setConfirmReopen(false)}
        onConfirm={() => {
          setConfirmReopen(false);
          qaResolved.mutate(false);
        }}
        title="Reopen this Q/A case?"
        message="Everyone following this plan who has Q/A alerts switched on will be emailed."
        confirmLabel="Reopen"
        variant="warning"
      />
    </div>
  );
}
